<template>
  <l-map
    ref="map"
    style="z-index: 0; height: 100%; width: 100%"
    :zoom="zoom"
    :center="center"
    @ready="onReady()"
    @update:zoom="zoomUpdated"
    @update:center="centerUpdated"
    @update:bounds="boundsUpdated"
  >
    <l-tile-layer :url="tileLayerUrl"></l-tile-layer>
    <l-circle-marker
      @click="$emit('MARKER_CLICKED', l)"
      @hover="$emit('MARKER_CLICKED', l)"
      v-for="(l, idx) in locations"
      :key="idx"
      :lat-lng="[+l.Lat, +l.Long]"
      color="skyblue"
      fillColor="skyblue"
      :fillOpacity="0.35"
      :stroke="false"
      :radius="l.radius"
    />
  </l-map>
</template>

<script>
import { LMap, LTileLayer, LCircleMarker } from 'vue2-leaflet';
import { mapState } from 'vuex';
import axios from 'axios';

export default {
  name: 'Map',
  props: [],
  data: () => ({
    url: 'https://tiles.stadiamaps.com/tiles/alidade_smooth_dark/{z}/{x}/{y}{r}.png',
    zoom: 2,
    minZoom: 1,
    center: [35.000074, 104.999927],
    bounds: null,
    newData: []
  }),
  computed: {
    ...mapState(['isDarkTheme']),
    locations() {
      console.log('newData at start of locations():', this.newData);
      // 不再回退到 cases.data，只使用 newData
      let dataSource = [];

      if (this.newData && this.newData.length > 0) {
        dataSource = this.newData;
      } else {
        return []; // 如果 newData 为空，则不显示任何标记
      }

      if (!dataSource || dataSource.length === 0) {
        return [];
      }

      // 处理 newData 格式：支持多种字段名
      const processedData = dataSource
        .map(item => {
          // 获取经纬度（支持多种字段名）
          const lat = item.Lat || item.lat || item.latitude || item.Latitude;
          const lng = item.Long || item.lng || item.longitude || item.Longitude || item.lon;

          // 获取数值（支持多种字段名）
          let confirmed = 0;
          if (item.dates && item.dates.length > 0) {
            // 旧格式：有 dates 数组
            confirmed = item.dates[item.dates.length - 1].confirmed || 0;
          } else {
            // 新格式：直接有数值字段
            confirmed = item.confirmed || item.value || item.count || item.total || 0;
          }

          return {
            ...item,
            Lat: lat,
            Long: lng,
            confirmed
          };
        })
        .filter(item => {
          // 过滤掉没有有效坐标或数值的数据
          return (
            item.Lat != null &&
            item.Long != null &&
            !Number.isNaN(item.Lat) &&
            !Number.isNaN(item.Long) &&
            item.confirmed > 0
          );
        });

      // 计算总确认数（用于计算半径）
      const totalConfirmed =
        processedData.reduce((sum, item) => sum + (item.confirmed || 0), 0) || 1;

      console.log('Processed locations:', processedData);
      console.log('Total confirmed for scaling:', totalConfirmed);

      return processedData.map(item => ({
        ...item,
        radius: this.scale({ confirmed: item.confirmed }, totalConfirmed)
      }));
    },
    tileLayerUrl() {
      const darkTheme = 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png';
      const lightTheme = 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png';
      return this.isDarkTheme ? darkTheme : lightTheme;
    }
  },
  mounted() {
    this.getUserLocation();
  },
  created() {
    axios
      .get('https://exhibition.qingyilian.com/api/bigdata/index')
      .then(res => {
        console.log('API response (res):', res);
        // 处理 API 返回的嵌套数据结构
        if (res.data && res.data.data && typeof res.data.data === 'object') {
          const allRankings = [];
          const regions = res.data.data; // 实际数据在 res.data.data
          Object.keys(regions).forEach(regionKey => {
            if (regions[regionKey] && regions[regionKey].ranking) {
              allRankings.push(...regions[regionKey].ranking);
            }
          });
          this.newData = allRankings
            .map(item => {
              // 确保经纬度字段为 Lat 和 Long，数值字段为 confirmed
              const mappedItem = {
                Lat: item.lat,
                Long: item.lon,
                confirmed: parseFloat(item.export) || 0, // 使用 export 作为主要数值
                ...item
              };
              console.log('Mapped item:', mappedItem); // 新增日志
              return mappedItem;
            })
            .filter(item => {
              const isValid =
                item.Lat != null &&
                item.Long != null &&
                !Number.isNaN(item.Lat) &&
                !Number.isNaN(item.Long) &&
                item.confirmed > 0;
              console.log('Filtering item:', item, 'IsValid:', isValid); // 新增日志
              return isValid;
            });
        } else {
          this.newData = [];
          console.warn('newData format not recognized or empty:', res.data);
        }
        console.log('newData after assignment in created():', this.newData);
      })
      .catch(error => {
        console.error('Failed to load newData (error):', error);
        this.newData = [];
      });
  },
  methods: {
    onReady() {
      this.$refs.map.mapObject.setMinZoom(2);
      this.$refs.map.mapObject.setMaxZoom(8);
      this.$refs.map.mapObject.fitWorld();
    },
    zoomUpdated(zoom) {
      this.zoom = zoom;
    },
    centerUpdated(center) {
      this.center = center;
    },
    boundsUpdated(bounds) {
      this.bounds = bounds;
    },
    flyTo(lat, lon) {
      this.$refs.map.mapObject.flyTo([lat, lon], 5);
    },
    scale(d, total_confirmed) {
      if (!d || Number.isNaN(d.confirmed) || d.confirmed <= 0 || total_confirmed <= 0) {
        return 0; // 返回0或者一个默认的最小半径
      }
      const min = 5;
      const factor = 2000;
      const zoomFactor = this.zoom >= 5 ? 1 : this.zoom / 10; // adjust divisor for best optics
      return Math.floor((d.confirmed * factor * zoomFactor) / total_confirmed) + min;
    },
    getUserLocation() {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(({ coords }) => {
          this.flyTo(coords.latitude, coords.longitude);
        });
      }
    }
  },
  components: {
    LMap,
    LTileLayer,
    LCircleMarker
  }
};
</script>
